import { useEffect, useRef, useState } from "react";
import {
  X,
  User,
  Settings,
  Mail,
  Phone,
  LogOut,
  Eye,
  EyeOff,
} from "lucide-react";
import { apiGet, apiPatch, apiPost } from "../../Services/Services";
import { ENDPOINTS } from "../../Services/Endpoints";
import { toast } from "react-toastify";
import {
  DefaultProfileImage,
  getProfileImageSrc,
} from "../../utils/profileImage";
import "./MyProfile.css";
import { useNavigate } from "react-router-dom";
const initialForm = {
  profile: {
    fullName: "",
    email: "",
    phone: "",
    profileImage: "",
    location: "",
    memberSince: "",
  },
  account: {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  },
};

function MyProfile({ open, onClose, onProfileUpdate }) {
  const [activeTab, setActiveTab] = useState("profile");
  const [form, setForm] = useState(initialForm);
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const fileInputRef = useRef(null);
  const [sessions, setSessions] = useState([]);

  const fetchProfileDetails = async () => {
    setIsLoadingProfile(true);
    try {
      const response = await apiGet(ENDPOINTS.profile.getDetail);
      const details = response?.data ?? {};
      const image = details.profileImage ?? "";

      setForm((prev) => ({
        ...prev,
        profile: {
          fullName: details.fullName ?? "",
          email: details.email ?? "",
          phone: String(details.phone ?? "")
            .replace(/\D/g, "")
            .slice(0, 10),
          profileImage: image,
          location: details.location ?? "",
          memberSince: details.memberSince ?? "",
        },
      }));

      if (onProfileUpdate) {
        onProfileUpdate(image);
      }
    } catch (error) {
      console.error("Error fetching profile details:", error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchProfileDetails();
      fetchSessionDetails();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  //   useEffect(() => {
  //   if (open) {
  //   }
  // }, [open]);

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setImageError("Please select a valid image file");
      e.target.value = "";
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setImageError("Image size must be less than 5MB");
      e.target.value = "";
      return;
    }

    setImageError("");
    const previewUrl = URL.createObjectURL(file);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return previewUrl;
    });

    setIsUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("profileImage", file);

      const response = await apiPatch(
        ENDPOINTS.profile.updateImage,
        formData,
        {},
        { showSuccessToast: false },
      );

      const updatedImage =
        response?.data?.profileImage ?? response?.profileImage ?? "";

      if (updatedImage) {
        setForm((prev) => ({
          ...prev,
          profile: {
            ...prev.profile,
            profileImage: updatedImage,
          },
        }));
        if (onProfileUpdate) {
          onProfileUpdate(updatedImage);
        }
        setImagePreview((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return "";
        });
      } else {
        await fetchProfileDetails();
        setImagePreview((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return "";
        });
      }

      toast.success(response?.message || "Profile image updated successfully");
    } catch (error) {
      console.error("Error uploading profile image:", error);
      setImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
    } finally {
      setIsUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    const trimmedPhone = form.profile.phone.trim();
    if (!trimmedPhone) {
      setProfileError("Mobile number is required");
      return;
    }
    if (!/^\d{10}$/.test(trimmedPhone)) {
      setProfileError("Mobile number must be exactly 10 digits");
      return;
    }

    setIsSavingProfile(true);
    setProfileError("");
    try {
      const response = await apiPatch(
        ENDPOINTS.profile.update,
        {
          phone: trimmedPhone,
          location: form.profile.location,
          fullName: form.profile.fullName,
          email: form.profile.email,
        },
        {},
        { showSuccessToast: false },
      );

      await fetchProfileDetails();
      onClose();
      toast.success(response?.message || "Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const [section, field] = name.split(".");

    if (!section || !field) return;

    const nextValue =
      section === "profile" && field === "phone"
        ? value.replace(/\D/g, "").slice(0, 10)
        : value;

    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: nextValue,
      },
    }));

    if (section === "account" && passwordErrors[field]) {
      setPasswordErrors((prev) => ({ ...prev, [field]: "" }));
    }

    if (section === "profile" && field === "phone" && profileError) {
      setProfileError("");
    }
  };

  const validatePasswordForm = () => {
    const { currentPassword, newPassword, confirmPassword } = form.account;
    const nextErrors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    if (!currentPassword.trim()) {
      nextErrors.currentPassword = "Current password is required";
    }

    if (!newPassword.trim()) {
      nextErrors.newPassword = "New password is required";
    } else if (newPassword.length < 8) {
      nextErrors.newPassword = "Password must be at least 8 characters";
    } else if (currentPassword && newPassword === currentPassword) {
      nextErrors.newPassword =
        "New password must be different from current password";
    }

    if (!confirmPassword.trim()) {
      nextErrors.confirmPassword = "Confirm password is required";
    } else if (newPassword && newPassword !== confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match";
    }

    setPasswordErrors(nextErrors);
    return (
      !nextErrors.currentPassword &&
      !nextErrors.newPassword &&
      !nextErrors.confirmPassword
    );
  };
  const navigate = useNavigate();

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    const { currentPassword, newPassword, confirmPassword } = form.account;

    setIsUpdatingPassword(true);
    try {
      const response = await apiPatch(
        ENDPOINTS.profile.changePassword,
        {
          currentPassword,
          newPassword,
          confirmPassword,
        },
        {},
        {
          showSuccessToast: false,
        },
      );

      setForm((prev) => ({
        ...prev,
        account: {
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        },
      }));
      setPasswordErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      onClose();
      toast.success(response?.message);
    } catch (error) {
      console.error("Error changing password:", error);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (!open) return null;

  const { profile, account } = form;

  const handleLogout = async () => {
    try {
      const response = await apiPost(
        ENDPOINTS.auth.logout,
        {},
        {},
        { showSuccessToast: false },
      );

      // localStorage.removeItem("access_token");
      localStorage.removeItem("persist:root");

      localStorage.removeItem("user");

      toast.success(response?.message || "Logout successfully");

      // window.location.href = "/login";
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const fetchSessionDetails = async () => {

    try {
      // const user = JSON.parse(localStorage.getItem("user") || "{}");
      //   const userId = user?.data?.id;
      const response = await apiGet(
        ENDPOINTS.profile.getSessions,
        {},
        {},
        {
          showSuccessToast: false,
          useApiSuccessMessage: false,
        },
      );

      const sessions = response?.data?.data ?? [];

      setSessions(sessions);
    } catch (error) {
      console.error("Error fetching session details:", error);
    }
  };

  const getHoursAgo = (date) => {
    const created = new Date(date);

    const now = new Date();

    const diff = now - created;

    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours === 0) {
      const minutes = Math.floor(diff / (1000 * 60));

      return `${minutes} minutes ago`;
    }

    return `${hours} hours ago`;
  };

  return (
    <div className="profile-overlay" onClick={onClose}>
      <aside className="profile-panel" onClick={(e) => e.stopPropagation()}>
        <div className="profile-panel-head">
          <div>
            <h2>My Profile</h2>
            <p>Manage your account settings and preferences</p>
          </div>
          <button type="button" className="profile-close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="profile-tabs">
          <button
            type="button"
            className={`profile-tab ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => setActiveTab("profile")}
          >
            <User size={14} />
            Profile
          </button>
          <button
            type="button"
            className={`profile-tab ${activeTab === "account" ? "active" : ""}`}
            onClick={() => setActiveTab("account")}
          >
            <Settings size={14} />
            Account
          </button>
        </div>

        <div className="profile-panel-body">
          {activeTab === "profile" ? (
            isLoadingProfile ? (
              <div className="profile-loading">Loading profile...</div>
            ) : (
              <form onSubmit={handleSaveProfile}>
                <div className="profile-hero">
                  <div className="profile-avatar">
                    <img
                      src={getProfileImageSrc(
                        imagePreview || profile.profileImage,
                      )}
                      alt={profile.fullName || "Profile"}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = DefaultProfileImage;
                      }}
                    />
                  </div>
                  <div>
                    <h3>{profile.fullName || "—"}</h3>
                    <p>{profile.email || "—"}</p>
                  </div>
                  <div className="profile-image-actions">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="profile-image-input"
                      onChange={handleImageSelect}
                      disabled={isUploadingImage || isSavingProfile}
                    />
                    <button
                      type="button"
                      className="profile-outline-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingImage || isSavingProfile}
                    >
                      {isUploadingImage ? "Uploading..." : "Change Photo"}
                    </button>
                  </div>
                </div>
                {imageError && (
                  <span className="profile-error-text">{imageError}</span>
                )}

                <div className="profile-field">
                  <label htmlFor="profile-fullName">Full Name</label>
                  <input
                    id="profile-fullName"
                    name="profile.fullName"
                    value={form.profile.fullName}
                    onChange={handleChange}
                    // className="profile-readonly-input"
                  />
                </div>

                <div className="profile-field">
                  <label htmlFor="profile-email">Email Address</label>

                  <div className="profile-input-icon">
                    <Mail size={16} />

                    <input
                      id="profile-email"
                      name="profile.email"
                      value={form.profile.email}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="profile-field">
                  <label htmlFor="profile-phone">
                    Mobile Number <span className="profile-required">*</span>
                  </label>
                  <div className="profile-input-icon">
                    <Phone size={16} />
                    <input
                      id="profile-phone"
                      name="profile.phone"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={form.profile.phone}
                      onChange={handleChange}
                      placeholder="Enter 10-digit mobile number"
                      className={profileError ? "profile-input-error" : ""}
                      disabled={isSavingProfile}
                    />
                  </div>
                  {profileError && (
                    <span className="profile-error-text">{profileError}</span>
                  )}
                </div>

                {/* <div className="profile-field">
              <label htmlFor="profile-location">Location</label>
              <input
                id="profile-location"
                name="profile.location"
                value={profile.location ?? ""}
                // onChange={(e) =>
                //   setProfile({
                //     ...profile,
                //     location: e.target.value
                //   })
                // }
                className="profile-readonly-input"
              />
              </div> */}
                <div className="profile-field">
                  <label htmlFor="profile-input-icon">Location</label>
                  <input
                    id="profile-location"
                    name="profile.location"
                    value={form.profile.location || "N/A"}
                    onChange={handleChange}
                    // className="profile-readonly-input"
                  />
                </div>
                <div className="profile-field">
                  <label htmlFor="profile-memberSince">Member Since</label>
                  <input
                    id="profile-memberSince"
                    name="profile.memberSince"
                    value={
                      form.profile.memberSince
                        ? new Date(
                            form.profile.memberSince,
                          ).toLocaleDateString()
                        : "n/a"
                    }
                    disabled
                    className="profile-readonly-input"
                  />
                </div>

                <button
                  type="submit"
                  className="profile-primary-btn"
                  disabled={isSavingProfile}
                >
                  {isSavingProfile ? "Saving..." : "Save Changes"}
                </button>
              </form>
            )
          ) : (
            <>
              <div className="profile-card">
                <h4>Change Password</h4>
                <form onSubmit={handleUpdatePassword}>
                  <div className="profile-field">
                    <label htmlFor="account-currentPassword">
                      Current Password{" "}
                      <span className="profile-required">*</span>
                    </label>
                    <div className="profile-password-wrap">
                      <input
                        id="account-currentPassword"
                        type={showCurrentPassword ? "text" : "password"}
                        name="account.currentPassword"
                        value={account.currentPassword}
                        onChange={handleChange}
                        className={
                          passwordErrors.currentPassword
                            ? "profile-input-error"
                            : ""
                        }
                        placeholder="Enter current password"
                        disabled={isUpdatingPassword}
                      />
                      <button
                        type="button"
                        className="profile-password-toggle"
                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                        aria-label="Toggle current password visibility"
                      >
                        {showCurrentPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                    {passwordErrors.currentPassword && (
                      <span className="profile-error-text">
                        {passwordErrors.currentPassword}
                      </span>
                    )}
                  </div>
                  <div className="profile-field">
                    <label htmlFor="account-newPassword">
                      New Password <span className="profile-required">*</span>
                    </label>
                    <div className="profile-password-wrap">
                      <input
                        id="account-newPassword"
                        type={showNewPassword ? "text" : "password"}
                        name="account.newPassword"
                        value={account.newPassword}
                        onChange={handleChange}
                        className={
                          passwordErrors.newPassword
                            ? "profile-input-error"
                            : ""
                        }
                        placeholder="Enter new password"
                        disabled={isUpdatingPassword}
                      />
                      <button
                        type="button"
                        className="profile-password-toggle"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        aria-label="Toggle new password visibility"
                      >
                        {showNewPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                    {passwordErrors.newPassword && (
                      <span className="profile-error-text">
                        {passwordErrors.newPassword}
                      </span>
                    )}
                  </div>
                  <div className="profile-field">
                    <label htmlFor="account-confirmPassword">
                      Confirm New Password{" "}
                      <span className="profile-required">*</span>
                    </label>
                    <div className="profile-password-wrap">
                      <input
                        id="account-confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        name="account.confirmPassword"
                        value={account.confirmPassword}
                        onChange={handleChange}
                        className={
                          passwordErrors.confirmPassword
                            ? "profile-input-error"
                            : ""
                        }
                        placeholder="Confirm new password"
                        disabled={isUpdatingPassword}
                      />
                      <button
                        type="button"
                        className="profile-password-toggle"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        aria-label="Toggle confirm password visibility"
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                    {passwordErrors.confirmPassword && (
                      <span className="profile-error-text">
                        {passwordErrors.confirmPassword}
                      </span>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="profile-primary-btn"
                    disabled={isUpdatingPassword}
                  >
                    {isUpdatingPassword ? "Updating..." : "Update Password"}
                  </button>
                </form>
              </div>
              {/* <div className="profile-card">
                <h4>Session Management</h4>
                <div className="profile-session">
                  <div>
                    <strong>Current Session</strong>
                    <p>Singapore · 192.168.1.100</p>
                  </div>
                  <span className="profile-session-active">Active</span>
                </div>
                <div className="profile-session">
                  <div>
                    <strong>Desktop · 2 hours ago</strong>
                    <p>192.168.1.105</p>
                  </div>
                  <button type="button" className="profile-outline-btn small">
                    Revoke
                  </button>
                </div>
              </div> */}
              .
              <div className="profile-card">
                <h4>Session Management</h4>

                {sessions.map((session) => (
                  <div className="profile-session" key={session.id}>
                    <div>
                      <strong>
                        {session.isActive
                          ? `Current Session · ${session.deviceName}`
                          : `${session.deviceName} · ${getHoursAgo(session.createdAt)}`}
                      </strong>

                      <p>
                        {session.location
                          ? `${session.location} · ${session.ipAddress}`
                          : session.ipAddress}
                      </p>
                    </div>

                    {session.isActive ? (
                      <span className="profile-session-active">Active</span>
                    ) : (
                      <button
                        type="button"
                        className="profile-outline-btn small"
                      >
                        inActive
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {/* <button type="button" className="profile-outline-btn profile-logout-btn">
                <LogOut size={16} />

              </button>
               */}
              <button
                type="button"
                className="profile-outline-btn profile-logout-btn"
                onClick={handleLogout}
              >
                <LogOut size={16} />
                Logout
              </button>
              <div className="profile-danger-zone">
                <h4>Danger Zone</h4>
                <p>Permanently delete your account and all associated data.</p>
                <button type="button" className="profile-outline-btn danger">
                  Delete Account
                </button>
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

export default MyProfile;
